import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import AddonsSection from '../components/AddonsSection';
import PricingSection from '../components/PricingSection';
import FAQSection from '../components/FAQSection';
import { 
  FiArrowRight, FiSend, FiUser, FiMail, 
  FiMessageSquare, FiCheckCircle, FiGlobe, FiZap, FiLayers 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const Home = () => {
  const [status, setStatus] = useState('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch("https://formspree.io/f/mdajvbbl", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const data = await response.json();
        alert(data.error || "Submission failed. Please try again.");
        setStatus('idle');
      }
    } catch (error) {
      alert("An error occurred during submission.");
      setStatus('idle');
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen">
      <Hero />

      <main className="max-w-7xl mx-auto px-6 pb-32">
        {/* Core Benefits Section */}
        <section className="py-32 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center border-b border-white/5">
          <div>
            <h2 className="text-[#CCFF00] font-mono-data text-xs font-black uppercase tracking-[0.4em] mb-6">Automated Reporting</h2>
            <h3 className="text-5xl md:text-6xl font-black text-white tracking-tighter uppercase leading-[0.95] mb-8">
              Keep your <br /> users updated <br /> 
              <span className="text-transparent" style={{ WebkitTextStroke: '1px #CCFF00' }}>With the latest</span>
            </h3>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              We deliver more than just scores. Our high-performance engine generates rapid updates to keep your readers informed and engaged around the clock.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <Link to="/news" className="bg-[#CCFF00] text-black px-8 py-4 text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all flex items-center justify-center gap-3">
                View News Feed <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {[
              { title: 'Global Feed', desc: 'Comprehensive and rapid reporting on global football events.', icon: FiGlobe },
              { title: 'Fast Delivery', desc: 'We dispatch news as soon as it enters our digital engine.', icon: FiZap },
              { title: 'High Volume', desc: 'Content generated specifically for high-traffic media platforms.', icon: FiLayers }
            ].map((benefit) => (
              <div key={benefit.title} className="bg-[#0A0A0A] p-8 border border-white/5 flex items-start gap-6 group hover:border-[#CCFF00] transition-colors">
                <div className="w-12 h-12 bg-white/5 flex items-center justify-center text-[#CCFF00] group-hover:bg-[#CCFF00] group-hover:text-black transition-all">
                  <SafeIcon icon={benefit.icon} className="text-xl" />
                </div>
                <div>
                  <h4 className="text-white font-black uppercase tracking-tighter text-xl mb-2">{benefit.title}</h4>
                  <p className="text-slate-500 text-sm font-medium">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing First */}
        <div id="pricing">
          <PricingSection showHeader={true} />
        </div>

        {/* Addons Second */}
        <AddonsSection />

        <FAQSection />

        <section id="contact" className="py-32 border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            <div>
              <h3 className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase leading-[0.9] mb-8">
                Contact <br /> <span className="text-transparent" style={{ WebkitTextStroke: '1px #CCFF00' }}>Desk</span>
              </h3>
              <p className="text-slate-400 text-lg mb-12 leading-relaxed max-w-md">
                Reach out to our team for inquiries regarding feed access, technical support, or partnership opportunities.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4 text-slate-500 mt-8">
                  <div className="w-2 h-2 bg-[#CCFF00] animate-pulse rounded-full"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest">Global Desk Active</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-card p-12 text-center h-full flex flex-col items-center justify-center border-[#CCFF00]"
                  >
                    <div className="w-16 h-16 bg-[#CCFF00] flex items-center justify-center mb-6">
                      <SafeIcon icon={FiCheckCircle} className="text-black text-3xl" />
                    </div>
                    <h4 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Message Dispatched</h4>
                    <p className="text-slate-500 text-sm mb-8">Your protocol has been received and queued for review.</p>
                    <button 
                      onClick={() => setStatus('idle')}
                      className="text-[#CCFF00] text-[10px] font-black uppercase tracking-widest border-b border-[#CCFF00] pb-1"
                    >
                      New Inquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass-card p-8 md:p-12 relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-[#CCFF00]"></div>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Full Name</label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                          <input 
                            type="text" 
                            required
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="John Doe"
                            className="w-full bg-white/5 border border-white/10 p-4 pl-12 text-white font-bold outline-none focus:border-[#CCFF00] transition-all placeholder:text-slate-700"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Your E-mail</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                          <input 
                            type="email" 
                            required
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="john@agency.com"
                            className="w-full bg-white/5 border border-white/10 p-4 pl-12 text-white font-bold outline-none focus:border-[#CCFF00] transition-all placeholder:text-slate-700"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Inquiry Details</label>
                        <div className="relative">
                          <SafeIcon icon={FiMessageSquare} className="absolute left-4 top-5 text-slate-600" />
                          <textarea 
                            rows="5" 
                            required
                            name="message"
                            value={formData.message}
                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                            placeholder="How can we help your platform?"
                            className="w-full bg-white/5 border border-white/10 p-4 pl-12 text-slate-300 font-medium outline-none focus:border-[#CCFF00] transition-all resize-none placeholder:text-slate-700"
                          />
                        </div>
                      </div>

                      <button 
                        type="submit" 
                        disabled={status === 'sending'}
                        className="w-full py-5 bg-[#CCFF00] text-black font-black text-[10px] uppercase tracking-[0.4em] hover:bg-white transition-all flex items-center justify-center gap-4 group"
                      >
                        {status === 'sending' ? (
                          <div className="w-4 h-4 border-2 border-black border-t-transparent animate-spin"></div>
                        ) : (
                          <>
                            Execute Dispatch
                            <SafeIcon icon={FiSend} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;